import React, { useState } from "react";
import axios from 'axios';
import { toast } from "react-toastify";

// Define a type for the different authentication steps
type AuthStep = "register" | "verifyRegisterOtp" | "login" | "verifyLoginOtp";

// Define the structure for a User
interface User {
  sub?: string;
  name: string;
  email: string;
  picture: string;
  id?: string;
  mobile?: string;
}

// Define the expected response structure from the backend
interface AuthResponse {
  message: string;
  id?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  profilePictureUrl?: string;
  mobile?: string;
  token?: string;
}

// Props for the AuthModal component
interface AuthModalProps {
  onClose: () => void;
  onLoginSuccess: (user: User) => void; 
}

export const AuthModal: React.FC<AuthModalProps> = ({ onClose, onLoginSuccess }) => {
  // State to manage the current step in the authentication flow
  const [step, setStep] = useState<AuthStep>("login");

  // State to store form data
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    otp: "",
  });

  // State to store validation errors
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    otp: "",
  });

  // State to hold the email for OTP verification
  const [otpEmail, setOtpEmail] = useState<string>("");

  // Function to reset all form data and errors
  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      otp: "",
    });
    setErrors({
      firstName: "",
      lastName: "",
      email: "",
      otp: "",
    });
    setOtpEmail("");
  };

  // --- Validation Functions ---

  const validateRegister = () => {
    const newErrors = { ...errors, firstName: "", lastName: "", email: "" };
    let valid = true;

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
      valid = false;
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
      valid = false;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const validateOtp = () => {
    const newErrors = { ...errors, otp: "" };
    let valid = true;

    if (!formData.otp.trim()) {
      newErrors.otp = "OTP is required";
      valid = false;
    } else if (!/^\d{6}$/.test(formData.otp)) {
      newErrors.otp = "OTP must be 6 digits";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const validateLoginEmail = () => {
    const newErrors = { ...errors, email: "" };
    let valid = true;

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  // --- Event Handlers for Form Submissions ---

  const handleRegisterSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateRegister()) {
      try {
        await axios.post("https://ayushvatika.com/api/auth/register-send-otp", {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
        });

        toast.success("OTP sent to your email! Please verify.");
        setOtpEmail(formData.email);
        setStep("verifyRegisterOtp");
        setFormData(prev => ({ ...prev, otp: "" }));
      } catch (error: any) {
        console.error("Failed to send registration OTP:", error);
        const errorMessage = error.response?.data?.message || "Failed to send OTP for registration!";
        toast.error(errorMessage);
      }
    }
  };

  const handleRegisterVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateOtp()) {
      try {
        const res = await axios.post<AuthResponse>("https://ayushvatika.com/api/auth/register-verify-otp", {
          email: otpEmail,
          otp: formData.otp,
        });

        toast.success(res.data.message || "Registration successful! Please login.");
        setStep("login");
        resetForm();
      } catch (error: any) {
        console.error("Failed to verify registration OTP:", error);
        const errorMessage = error.response?.data?.message || "Failed to verify OTP!";
        toast.error(errorMessage);
      }
    }
  };

  const handleLoginSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateLoginEmail()) {
      try {
        await axios.post("https://ayushvatika.com/api/auth/login-send-otp", {
          email: formData.email,
        });

        toast.success("OTP sent to your email! Please verify to login.");
        setOtpEmail(formData.email);
        setStep("verifyLoginOtp");
        setFormData(prev => ({ ...prev, otp: "" }));
      } catch (error: any) {
        console.error("Login Error:", error);
        let errorMessage = "Failed to send OTP for login!";
        if (error.response && error.response.data) {
            if (typeof error.response.data === 'string') {
                errorMessage = error.response.data;
            } else if (error.response.data.message) {
                errorMessage = error.response.data.message;
            }
        }
        toast.error(errorMessage);
        setErrors(prev => ({ ...prev, email: errorMessage }));
      }
    }
  };

  const handleLoginVerifyOtp = async (e: React.FormEvent) => {
      e.preventDefault();
      if (validateOtp()) {
          try {
              const res = await axios.post<AuthResponse>("https://ayushvatika.com/api/auth/login-verify-otp", {
                  email: otpEmail,
                  otp: formData.otp,
              });

              if (res.data && res.data.email) {
                  if (res.data.token) {
                      localStorage.setItem("token", res.data.token);
                      window.dispatchEvent(new Event("storage"));
                  }

                  const fullName = `${res.data.firstName || ''} ${res.data.lastName || ''}`.trim();
                  
                  const userForStorage: User = {
                      sub: res.data.email,
                      name: fullName || res.data.email,
                      email: res.data.email,
                      picture: res.data.profilePictureUrl || "/default-avatar.png",
                      id: res.data.id,
                      mobile: res.data.mobile,
                  };

                  localStorage.setItem("user", JSON.stringify(userForStorage));
                  toast.success(res.data.message || "Login successful!");
                  
                  // Call the updated prop
                  onLoginSuccess(userForStorage);
                  
                  onClose();
                  resetForm();
              } else {
                  toast.error("Invalid response from server during login verification.");
              }
          } catch (error: any) {
              console.error("Login verification failed:", error);
              const errorMessage = error.response?.data?.message || "Login failed! Please check your OTP.";
              toast.error(errorMessage);
          }
      }
  };

  // --- Render Logic ---

  const renderForm = () => {
    switch (step) {
      case "register":
        return (
          <form onSubmit={handleRegisterSendOtp} className="space-y-4">
            <input
              type="text"
              placeholder="First Name"
              className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            />
            {errors.firstName && <p className="text-red-600 text-sm">{errors.firstName}</p>}

            <input
              type="text"
              placeholder="Last Name"
              className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            />
            {errors.lastName && <p className="text-red-600 text-sm">{errors.lastName}</p>}

            <input
              type="email"
              placeholder="Email"
              className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            {errors.email && <p className="text-red-600 text-sm">{errors.email}</p>}

            <button
              type="submit"
              className="w-full bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-700 transition duration-200"
            >
              Register & Send OTP
            </button>
          </form>
        );

      case "verifyRegisterOtp":
        return (
          <form onSubmit={handleRegisterVerifyOtp} className="space-y-4">
            <p className="text-center text-gray-700">
              An OTP has been sent to <span className="font-semibold">{otpEmail}</span>. Please enter it below.
            </p>
            <input
              type="text"
              placeholder="Enter OTP"
              className="w-full border rounded-lg px-4 py-2 text-center text-lg tracking-widest focus:ring-emerald-500 focus:border-emerald-500"
              value={formData.otp}
              onChange={(e) => setFormData({ ...formData, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              maxLength={6}
            />
            {errors.otp && <p className="text-red-600 text-sm text-center">{errors.otp}</p>}

            <button
              type="submit"
              className="w-full bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-700 transition duration-200"
            >
              Verify OTP & Complete Registration
            </button>
            <button
              type="button"
              onClick={() => {
                setStep("register");
                setFormData(prev => ({ ...prev, otp: "" }));
              }}
              className="w-full text-emerald-600 py-2 mt-2 hover:underline"
            >
              Back to Registration
            </button>
          </form>
        );

      case "login":
        return (
          <form onSubmit={handleLoginSendOtp} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            {errors.email && <p className="text-red-600 text-sm">{errors.email}</p>}

            <button
              type="submit"
              className="w-full bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-700 transition duration-200"
            >
              Login & Send OTP
            </button>
          </form>
        );

      case "verifyLoginOtp":
        return (
          <form onSubmit={handleLoginVerifyOtp} className="space-y-4">
            <p className="text-center text-gray-700">
              An OTP has been sent to <span className="font-semibold">{otpEmail}</span>. Please enter it below to login.
            </p>
            <input
              type="text"
              placeholder="Enter OTP"
              className="w-full border rounded-lg px-4 py-2 text-center text-lg tracking-widest focus:ring-emerald-500 focus:border-emerald-500"
              value={formData.otp}
              onChange={(e) => setFormData({ ...formData, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              maxLength={6}
            />
            {errors.otp && <p className="text-red-600 text-sm text-center">{errors.otp}</p>}

            <button
              type="submit"
              className="w-full bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-700 transition duration-200"
            >
              Verify OTP & Login
            </button>
            <button
              type="button"
              onClick={() => {
                setStep("login");
                setFormData(prev => ({ ...prev, otp: "" }));
              }}
              className="w-full text-emerald-600 py-2 mt-2 hover:underline"
            >
              Back to Login
            </button>
          </form>
        );

      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (step) {
      case "register":
        return "Create Your Account";
      case "verifyRegisterOtp":
        return "Verify Registration OTP";
      case "login":
        return "Welcome Back";
      case "verifyLoginOtp":
        return "Verify Login OTP";
      default:
        return "Authentication";
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 px-4 font-inter">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-red-600 text-xl font-bold"
        >
          ✕
        </button>

        <h2 className="text-2xl font-bold text-center text-emerald-700 mb-6">
          {getTitle()}
        </h2>

        {renderForm()}

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            {(step === "register" || step === "verifyRegisterOtp") ? (
              <>
                Already have an account?{" "}
                <button
                  onClick={() => {
                    setStep("login");
                    resetForm();
                  }}
                  className="text-emerald-600 hover:underline"
                >
                  Login here
                </button>
              </>
            ) : (
              <>
                New to AyushVatika?{" "}
                <button
                  onClick={() => {
                    setStep("register");
                    resetForm();
                  }}
                  className="text-emerald-600 hover:underline"
                >
                  Register now
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;